import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  CheckCircle2,
  Clock3,
  AlertTriangle,
  ListChecks,
  ArrowUpRight,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = {
  new:         "#38BDF8",
  in_progress: "#F59E0B",
  done:        "#10B981",
  stuck:       "#F43F5E",
  planning:    "#A855F7",
};

const STATUS_LABELS = {
  new: "New",
  in_progress: "In Progress",
  done: "Done",
  stuck: "Stuck",
  planning: "Planning",
};

function Kpi({ label, value, icon: Icon, tint, sub, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-slate-300 transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</span>
          <span className="font-display text-4xl font-light tracking-tighter text-slate-900 mt-1">{value}</span>
          {sub && <span className="text-xs text-slate-500">{sub}</span>}
        </div>
        <div className={`h-10 w-10 rounded-xl ${tint} flex items-center justify-center`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/tasks"),
      ]);
      setStats(s.data);
      setRecent(
        [...t.data]
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 5)
      );
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  const pieData = stats
    ? Object.entries(stats.status_counts)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v, key: k }))
    : [];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Greeting */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {greeting}, {user?.name?.split(" ")[0] || "there"}.
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Here's what's happening across your workspace today.
          </p>
        </div>
        <a
          href="/tasks"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900"
          data-testid="dashboard-open-tasks"
        >
          Open Tasks <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Kpi
          label="Total tasks"
          value={stats?.total ?? "–"}
          icon={ListChecks}
          tint="bg-sky-50 text-sky-700"
          sub="All statuses"
          testid="kpi-total"
        />
        <Kpi
          label="Completed"
          value={stats?.completed ?? "–"}
          icon={CheckCircle2}
          tint="bg-mint-50 text-emerald-700"
          sub={`${stats?.completion_rate ?? 0}% completion rate`}
          testid="kpi-completed"
        />
        <Kpi
          label="In progress"
          value={stats?.in_progress ?? "–"}
          icon={Clock3}
          tint="bg-butter-50 text-amber-700"
          sub="Active right now"
          testid="kpi-in-progress"
        />
        <Kpi
          label="Needs attention"
          value={stats?.stuck ?? "–"}
          icon={AlertTriangle}
          tint="bg-rose-50 text-rose-700"
          sub="Stuck tasks"
          testid="kpi-stuck"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6" data-testid="chart-trend">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-medium text-slate-900">Activity this week</h3>
              <p className="text-xs text-slate-500">Tasks created per day</p>
            </div>
          </div>
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.trend || []} barSize={22}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="day" stroke="#94A3B8" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis stroke="#94A3B8" tickLine={false} axisLine={false} fontSize={12} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #E2E8F0",
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="tasks" fill="url(#brandGradient)" radius={[8, 8, 0, 0]} />
                <defs>
                  <linearGradient id="brandGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" />
                    <stop offset="100%" stopColor="#38BDF8" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status pie */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="chart-status">
          <h3 className="font-display text-lg font-medium text-slate-900">Status distribution</h3>
          <p className="text-xs text-slate-500 mb-2">Across all tasks</p>
          <div className="h-60">
            {pieData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                  >
                    {pieData.map((d) => (
                      <Cell key={d.key} fill={STATUS_COLORS[d.key]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #E2E8F0",
                      fontSize: 12,
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle"
                    wrapperStyle={{ fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-slate-400">
                No tasks yet
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="recent-activity">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-medium text-slate-900">Recent activity</h3>
            <p className="text-xs text-slate-500">Latest updates across your tasks</p>
          </div>
        </div>
        <ul className="divide-y divide-slate-100">
          {recent.length === 0 && (
            <li className="px-6 py-10 text-center text-sm text-slate-400">No recent activity yet</li>
          )}
          {recent.map((t) => (
            <li key={t.id} className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors">
              <div
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: STATUS_COLORS[t.status] }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{t.title}</p>
                <p className="text-xs text-slate-500">{STATUS_LABELS[t.status]}{t.assignee ? ` · ${t.assignee}` : ""}</p>
              </div>
              <span className="text-xs text-slate-400 shrink-0">
                {new Date(t.updated_at).toLocaleDateString()}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
